import InfoIcon from "@mui/icons-material/Info";
import { Box, Divider, Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import useColors from "../../../../../../api/hooks/useColors";
import { entityKeys } from "../../../../../../api/queryKeys";
import CustomToolTip from "../../../../../../components/CustomTooltip";
import LabelComponentProp from "../../../../../../components/LabelComponentProp";
import { BootstrapContext } from "../../../../../../contexts/BootstrapContext";
import {
  getPrettyDateTime,
  useLocalTime,
} from "../../../../../../utils/timestamp";
import ChipColor from "../../../../../remediation/plan/components/ChipColor";
import { isObject } from "../../../../../stack/environment/utils";
import IntegrationImage from "../../../../../stack/integrations/components/IntegrationImage";
import CustomLabel from "../../../../frameworks/components/CustomLabel";
import ControlStatusIndicator from "../../ControlStatusIndicator";
import CustomSeveritySelect from "../../CustomSeveritySelect";
import NISTLabel from "../../NISTLabel";
import ImpactChip from "./ImpactChip";

const ControlDetailsSection = ({ fullControl, demoMode }) => {
  const queryClient = useQueryClient();
  const { user } = useContext(BootstrapContext);

  // Asked by AIPAC.
  const ALLOWED_CUSTOMER_IDS = ["breeze-security.com", "aipac.org"];
  const customerId = user?.customer_id;
  const shouldShowImpactTooltip = ALLOWED_CUSTOMER_IDS.includes(customerId);

  const ToolBox = (
    <Grid item>
      <LabelComponentProp
        label={"Tool"}
        textTypography="caption"
        sx={{
          display: "flex",
          alignItems: "left",
        }}
      >
        <IntegrationImage
          name={fullControl?.tool.toLowerCase()}
          width={"24px"}
          newLogo={true}
        />
      </LabelComponentProp>
    </Grid>
  );

  const SeverityBox = (
    <Grid item alignContent={"center"}>
      <LabelComponentProp
        label={"Severity"}
        textTypography="caption"
        sx={{
          display: "flex",
          alignItems: "left",
        }}
      >
        <CustomSeveritySelect
          row={fullControl}
          renderValue={(customSeverity) => (
            <ChipColor
              value={customSeverity}
              type={"controlPriority"}
              disabled={!fullControl?.is_gap}
            />
          )}
        />
      </LabelComponentProp>
    </Grid>
  );

  const colors = useColors();
  const showLocalTime = useLocalTime();

  const StatusBox = (
    <Grid item alignContent={"center"}>
      <LabelComponentProp
        label={"Status"}
        textTypography="caption"
        sx={{
          display: "flex",
          alignItems: "left",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ControlStatusIndicator status={fullControl?.status} />
          {fullControl?.is_snoozed && fullControl?.snooze_details && (
            <CustomToolTip
              title={
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    Snoozed On:{" "}
                  </Typography>

                  <Typography variant="caption">
                    {getPrettyDateTime(
                      fullControl.snooze_details.snoozed_at,
                      showLocalTime
                    )}
                    {fullControl.snooze_details.snoozed_by_name &&
                      ` by ${fullControl.snooze_details.snoozed_by_name}`}
                  </Typography>
                  <br />
                  {fullControl?.snooze_details?.snoozed_until && (
                    <>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 500, mt: 0.5 }}
                      >
                        Snoozed Until:{" "}
                      </Typography>
                      <Typography variant="caption">
                        {getPrettyDateTime(
                          fullControl.snooze_details.snoozed_until,
                          showLocalTime
                        )}
                      </Typography>
                      <br />
                    </>
                  )}
                  {fullControl.snooze_details.snooze_reason && (
                    <>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 500, mt: 0.5 }}
                      >
                        Reason:{" "}
                      </Typography>
                      <Typography variant="caption">
                        {fullControl.snooze_details.snooze_reason}
                      </Typography>
                    </>
                  )}
                </Box>
              }
              placement="top"
            >
              <InfoIcon
                sx={{
                  width: "16px",
                  height: "16px",
                  color: colors.tooltip_info_icon,
                  cursor: "pointer",
                }}
              />
            </CustomToolTip>
          )}
        </Box>
      </LabelComponentProp>
    </Grid>
  );

  const DaysOpenBox = () => {
    if (
      !fullControl?.first_seen ||
      !isObject(fullControl.first_seen) ||
      !fullControl.first_seen.hasOwnProperty("timestamp_utc")
    ) {
      return null;
    }

    // Calculate days open since first_seen
    const firstSeenTimestamp = fullControl.first_seen.timestamp_utc;
    const currentTime = Math.floor(Date.now() / 1000);
    const daysDifference = Math.floor(
      (currentTime - firstSeenTimestamp) / (24 * 60 * 60)
    );

    const daysText = daysDifference === 1 ? "1 day" : `${daysDifference} days`;

    return (
      <Grid item alignContent={"center"}>
        <LabelComponentProp
          label={"Days Open"}
          textTypography="caption"
          sx={{
            display: "flex",
            alignItems: "left",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="caption" sx={{ color: colors.text_secondary }}>
              {daysText}
            </Typography>
            <CustomToolTip
              title={
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    Discovered On:{" "}
                  </Typography>
                  <Typography variant="caption">
                    {getPrettyDateTime(
                      fullControl.first_seen.timestamp_utc,
                      showLocalTime
                    )}
                  </Typography>
                  <br />
                  {fullControl?.last_seen &&
                    isObject(fullControl.last_seen) &&
                    fullControl.last_seen.hasOwnProperty("timestamp_utc") && (
                      <>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 500, mt: 0.5 }}
                        >
                          Validated On:{" "}
                        </Typography>
                        <Typography variant="caption">
                          {getPrettyDateTime(
                            fullControl.last_seen.timestamp_utc,
                            showLocalTime
                          )}
                        </Typography>
                      </>
                    )}
                </Box>
              }
              placement="top"
            >
              <InfoIcon
                sx={{
                  width: "16px",
                  height: "16px",
                  color: colors.tooltip_info_icon,
                  cursor: "pointer",
                }}
              />
            </CustomToolTip>
          </Box>
        </LabelComponentProp>
      </Grid>
    );
  };

  const NISTBox = (
    <Grid item alignContent={"center"}>
      <LabelComponentProp
        label={"NIST CSF"}
        textTypography="caption"
        sx={{
          display: "flex",
          alignItems: "left",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <NISTLabel nistFunction={fullControl?.nist_function} />
          {fullControl?.nist_identifiers &&
            fullControl.nist_identifiers.length > 0 &&
            fullControl?.nist_categories &&
            fullControl.nist_categories.length > 0 && (
              <CustomToolTip
                title={
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      paddingY: 0.5,
                    }}
                  >
                    {Array.from(fullControl.nist_identifiers).map(
                      (identifier, index) => {
                        const categoryName = Array.from(
                          fullControl.nist_categories
                        )[index];
                        return (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <CustomLabel
                              text={identifier}
                              colorFamily="NIST"
                              severity={fullControl.nist_function}
                              labelType="outlined"
                              size="small"
                            />
                            <Typography variant="caption">
                              {categoryName}
                            </Typography>
                          </Box>
                        );
                      }
                    )}
                  </Box>
                }
                placement="right"
              >
                <InfoIcon
                  sx={{
                    width: "16px",
                    height: "16px",
                    color: colors.tooltip_info_icon,
                    cursor: "pointer",
                  }}
                />
              </CustomToolTip>
            )}
        </Box>
      </LabelComponentProp>
    </Grid>
  );

  const allEntitiesData = queryClient.getQueryData(entityKeys.allEntities());
  const affectedEntityTypes = fullControl?.affected_entity_counts_by_type
    ? Object.keys(fullControl.affected_entity_counts_by_type)
    : [];

  let totalLoadedEntities = 0;
  let hasAnyLoadedTotal = false;

  if (Array.isArray(allEntitiesData) && affectedEntityTypes.length > 0) {
    const entityCountsByType = {};
    allEntitiesData.forEach((entity) => {
      const entityType = entity.entity_type;
      if (affectedEntityTypes.includes(entityType)) {
        entityCountsByType[entityType] =
          (entityCountsByType[entityType] || 0) + 1;
      }
    });

    totalLoadedEntities = Object.values(entityCountsByType).reduce(
      (sum, count) => sum + count,
      0
    );
    hasAnyLoadedTotal = totalLoadedEntities > 0;
  }

  const ImpactBox = (
    <Grid item>
      <LabelComponentProp
        sx={{
          display: "flex",
          alignItems: "left",
        }}
        label={"Impact"}
        textTypography="caption"
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ImpactChip
            numberOfEntities={fullControl?.impacted_included_entities_count}
          />
          {shouldShowImpactTooltip &&
            hasAnyLoadedTotal &&
            totalLoadedEntities > 0 && (
              <CustomToolTip
                title={
                  <Typography variant="caption">
                    Out of {totalLoadedEntities}{" "}
                    {totalLoadedEntities === 1 ? "entity" : "entities"}
                  </Typography>
                }
                placement="top"
              >
                <InfoIcon
                  sx={{
                    width: "16px",
                    height: "16px",
                    color: colors.tooltip_info_icon,
                    cursor: "pointer",
                  }}
                />
              </CustomToolTip>
            )}
        </Box>
      </LabelComponentProp>
    </Grid>
  );

  return (
    <Grid container gap={4} alignItems={"center"} sx={{ pl: 2 }}>
      {ToolBox}
      <Divider orientation="vertical" flexItem />
      {SeverityBox}
      <Divider orientation="vertical" flexItem />
      {StatusBox}
      <Divider orientation="vertical" flexItem />
      {NISTBox}
      <Divider orientation="vertical" flexItem />
      {ImpactBox}
      {DaysOpenBox() && (
        <>
          <Divider orientation="vertical" flexItem />
          {DaysOpenBox()}
        </>
      )}
    </Grid>
  );
};

export default ControlDetailsSection;
